import React, { useCallback, useRef, useState, CSSProperties, useEffect } from 'react';
import { useApiDatatable } from '../hooks/useApiDatatable';
import { useToasts } from 'app/components/Toast';
import { DataTable } from 'primereact/datatable';
import { locationService } from './../../../services/LocationService';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { ApiConfiguration } from '../types';
import { Column, ColumnBodyType, ColumnProps } from 'primereact/column';
import { useDeleteApiConfigurationMutation } from '../services/apiManagementApi';
import { OverlayMenu, PrimeTable } from 'components';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Card, CardBody, Container } from 'reactstrap';
import { useAppDispatch } from 'app/store';
import { setIsEditingPage } from 'app/reducers/pageSlice';

interface Props {
	column?: ColumnProps;
}

const ApiManagementPage: React.FC<Props> = () => {

	const dispatch = useAppDispatch()

	useEffect(() => {
		dispatch(setIsEditingPage(false)) // disable editing state in order to show add menu button
	},[dispatch])

	const toast = useToasts();
	const { data, isLoading, totalRecords, rows, first, filters, sortField, sortOrder, onPage, onSort, onFilter } =
		useApiDatatable();

	const [selectedRows, setSelectedRows] = useState(null);
	const [globalFilter, setGlobalFilter] = useState(null);
	const dt = useRef<DataTable>(null);

	const history = locationService.getHistory();

	const [deleteApi] = useDeleteApiConfigurationMutation();

	const [showDialog, setShowDialog] = useState(false);

	const _handleDelete = useCallback(
		(id: number | string) => {
			confirmDialog({
				message: `Do you want to delete this record with ID: ${id}?`,
				header: 'Delete Confirmation',
				icon: 'pi pi-info-circle',
				acceptClassName: 'p-button-danger',
				accept: async () =>
					deleteApi(id)
						.unwrap()
						.then(() => {
							toast.toastSuccess('Success', `Api Management deleted.`);
						})
						.catch((error) => {
							toast.toastError(error?.data?.error, error?.data?.message);
						}),
			});
		},
		[deleteApi, toast],
	);

	const _handleEdit = useCallback(
		(id: number | string) => {
			history.push({ pathname: `/endpoint-api-management/edit/${id}` });
		},
		[history],
	);

	const columns: ColumnProps[] = React.useMemo(() => {
		const actionTemplateBody = (rowData: ApiConfiguration): ColumnBodyType => {
			const items = [
				{
					label: 'Edit',
					icon: 'mdi mdi-pencil',
					command: () => _handleEdit(rowData.id),
				},
				{
					label: 'Delete',
					icon: 'mdi mdi-delete',
					command: () => _handleDelete(rowData.id),
				},
			];
			return (
				<React.Fragment>
					<OverlayMenu id={`api-rule-${rowData.id}`} items={items} />
				</React.Fragment>
			);
		};

		return [
			{
				field: 'id',
				header: 'ID',
			},
			{
				field: 'name',
				header: 'NAME',
			},
			{
				field: 'description',
				header: 'DESCRIPTION',
			},
			{
				field: 'requestMethod',
				header: 'METHOD',
			},
			{
				field: 'urlEndpoint',
				header: 'URL ENDPOINT',
			},

			{
				headerStyle: {
					width: '5em',
				} as CSSProperties,
				body: actionTemplateBody,
				bodyStyle: {
					textAlign: 'center',
					overflow: 'visible',
				} as CSSProperties,
			},
		];
	}, [_handleDelete, _handleEdit]);

	const createApiManagement = () => {
		history.push({
			pathname: '/endpoint-api-management/new',
		});
	};

	const renderHeader = () => {
		return (
			<div className="table-header">
				<span className="p-input-icon-left">
					<i className="pi pi-search" />
					<InputText
						type="search"
						onInput={(e: any) => setGlobalFilter(e.target.value)}
						placeholder="Search"
					/>
				</span>

				<Button
					label="Add New"
					icon="mdi mdi-plus-circle"
					iconPos="right"
					className="p-button-link text-default"
					onClick={createApiManagement}
				/>
			</div>
		);
	};

	const renderColumns = () => {
		return columns && columns.map((column: ColumnProps, i) => <Column key={i} {...column} />);
	};

	const header = renderHeader();

	return (
		<Container fluid>
			<h2 className="my-3">All APIs</h2>
			<div>
				<Card>
					<CardBody>
						<PrimeTable
							lazy
							ref={dt}
							value={data}
							header={header}
							dataKey="id"
							globalFilter={globalFilter}
							selection={selectedRows}
							onSelectionChange={(e) => setSelectedRows(e.value)}
							rows={rows}
							first={first}
							sortField={sortField}
							sortOrder={sortOrder}
							loading={isLoading}
							totalRecords={totalRecords}
							filters={filters}
							onPage={onPage}
							onSort={onSort}
							onFilter={onFilter}
						>
							{renderColumns()}
						</PrimeTable>
						<ConfirmDialog visible={showDialog} onHide={() => setShowDialog(false)} />
					</CardBody>
				</Card>
			</div>
		</Container>
	);
};

export default ApiManagementPage;
